import React, { useEffect, useRef, useState, useCallback } from "react";
import "ol/ol.css";
import Map from "ol/Map";
import View from "ol/View";
import { Tile as TileLayer, Vector as VectorLayer } from "ol/layer";
import { getDistance } from "ol/sphere";
import { Vector as VectorSource } from "ol/source";
import { Point, LineString } from "ol/geom";
import { Style, Stroke, Icon, Fill, Circle as CircleStyle, Text } from "ol/style";
import { Feature } from "ol";
import { fromLonLat } from "ol/proj";
import { GeoData } from "@/types/GeoData";
import { Tooltip } from "@/components/ui/tooltip";
import { XYZ } from "ol/source";
import { Route } from "@/types/routes";

const OUTLIER_THRESHOLD = 300


const colorMap: Record<string, string[]> = {
    "red": ["#E53935", "#D32F2F", "#C62828", "#B71C1C", "#7F0000"],
    "green": ["#43A047", "#388E3C", "#2E7D32", "#1B5E20", "#004D00"],
    "blue": ["#1E88E5", "#1976D2", "#1565C0", "#0D47A1", "#002171"]
};

const defaultColor = "#9209e3"

const getRandomShade = (baseColor: string, index: number) => {
    const shades = colorMap[baseColor] || colorMap.blue;
    return shades[index % shades.length];
};

const getColorForType = (type: string) => {
    if (type.toLowerCase().includes("security")) return "red";
    if (type.toLowerCase().includes("public")) return "green";
    return "blue";
};

const getSvgIcon = (type: string, color: string) => {
    let iconPath = `<path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/>`; // По умолчанию иконка
    if (type.toLowerCase().includes("security")) {
        iconPath = `<path d="M10 2h4"/><path d="m21 8-2 2-1.5-3.7A2 2 0 0 0 15.646 5H8.4a2 2 0 0 0-1.903 1.257L5 10 3 8"/><path d="M7 14h.01"/><path d="M17 14h.01"/><rect width="18" height="8" x="3" y="10" rx="2"/><path d="M5 18v2"/><path d="M19 18v2"/>`; // CarTaxiFront
    } else if (type.toLowerCase().includes("public")) {
        iconPath = `<path d="M8 6v6"/><path d="M15 6v6"/><path d="M2 12h19.6"/><path d="M18 18h3s.5-1.7.8-2.8c.1-.4.2-.8.2-1.2 0-.4-.1-.8-.2-1.2l-1.4-5C20.1 6.8 19.1 6 18 6H4a2 2 0 0 0-2 2v10h3"/><circle cx="7" cy="18" r="2"/><path d="M9 18h5"/><circle cx="16" cy="18" r="2"/>`; // Bus
    }
    const svgString = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"  fill="none" stroke="${color}"  stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-bus">
        ${iconPath}
    </svg>`;

    return `data:image/svg+xml;base64,${btoa(svgString)}`;
};



class RouteRenderer {
    private layer: VectorLayer<VectorSource>;

    constructor(private map: Map, private route: Route) {
        this.layer = new VectorLayer({ source: new VectorSource() });
        this.map.getLayers().insertAt(this.map.getLayers().getLength() - 1, this.layer);
        this.render();
    }

    render() {
        const source = this.layer.getSource();
        if (source) {
            source.clear();

            const line = new LineString(this.route.points.map((p) => fromLonLat([p.longitude, p.latitude])));
            const routeFeature = new Feature({ geometry: line });
            routeFeature.setStyle(new Style({ stroke: new Stroke({ color: this.route.color, width: 4 }) }));
            source.addFeature(routeFeature);
        }
    }

    destroy() {
        this.map.removeLayer(this.layer);
    }
}



const formatMoscowTime = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    return new Intl.DateTimeFormat("ru-RU", {
        timeZone: "Europe/Moscow",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    }).format(date);
};

interface GeoMapProps {
    deviceTypes: string[];
    newPoint: GeoData | null;
    routes?: Route[];
}

const GeoMap: React.FC<GeoMapProps> = ({ deviceTypes, newPoint, routes = [] }) => {
    const mapRef = useRef<Map | null>(null);
    const mapContainerRef = useRef<HTMLDivElement | null>(null);

    const vectorSource = useRef(new VectorSource());
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [deviceTracks, setDeviceTracks] = useState<Record<string, Feature<Point>[]>>({});
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [historyPoints, setHistoryPoints] = useState<Record<string, GeoData[]>>({});
    const [tooltipContent, setTooltipContent] = useState<string | null>(null);
    const [tooltipPosition, setTooltipPosition] = useState<[number, number] | null>(null);
    const [deviceColors, setDeviceColors] = useState<Record<string, string>>({});
    const routeRenderers = useRef<RouteRenderer[]>([]);

    const hiddenTracks = useRef<Record<string, Feature[]>>({});


    const addTrackPointsToMap = useCallback((points: GeoData[]) => {
        if (!mapRef.current || !vectorSource.current) return;

        points.forEach((newPoint) => {
            const { latitude, longitude, timestamp, device, device_name, type } = newPoint;
            const coordinates = fromLonLat([longitude, latitude]);

            let trackColor = newPoint.device_color && newPoint.device_color.trim() !== ""
                ? newPoint.device_color
                : deviceColors[device];

            if (!trackColor) {
                const baseColor = getColorForType(type);
                const colorIndex = Object.keys(deviceColors).length;
                trackColor = getRandomShade(baseColor, colorIndex);

                setDeviceColors((prevColors) => ({
                    ...prevColors,
                    [device]: trackColor,
                }));
            }

            const IconComponent = (newPoint.device_icon && newPoint.device_icon.length > 0)
                ? `data:image/svg+xml;base64,${btoa(newPoint.device_icon)}`
                : getSvgIcon(type, trackColor);

            const pointFeature = new Feature(new Point(coordinates));
            pointFeature.set("timestamp", timestamp);
            pointFeature.set("isPoint", true);
            pointFeature.set("color", trackColor);
            pointFeature.set("device", device);
            pointFeature.set("deviceType", type);

            pointFeature.setStyle(
                new Style({
                    image: new CircleStyle({
                        radius: 6,
                        fill: new Fill({ color: trackColor || defaultColor }),
                        stroke: new Stroke({ color: "white", width: 1 }),
                    }),
                })
            );

            setDeviceTracks((prevTracks) => {
                const updatedTracks = { ...prevTracks, [device]: [...(prevTracks[device] || []), pointFeature] };

                if (updatedTracks[device].length > 1) {
                    const lineFeature = new Feature(
                        new LineString(updatedTracks[device].map((p) => p.getGeometry()?.getCoordinates() || coordinates))
                    );
                    lineFeature.set("isLine", true);
                    lineFeature.set("device", device);
                    lineFeature.set("deviceType", type);
                    lineFeature.setStyle(
                        new Style({
                            stroke: new Stroke({
                                color: trackColor,
                                width: 2,
                            }),
                        })
                    );
                    vectorSource.current.addFeature(lineFeature);
                }

                return updatedTracks;
            });

            vectorSource.current.getFeatures().forEach((feature) => {
                if (feature.get("device") === device && feature.get("isIcon")) {
                    vectorSource.current.removeFeature(feature);
                }
            });

            const iconFeature = new Feature(new Point(coordinates));
            iconFeature.set("device", device);
            iconFeature.set("isIcon", true);
            iconFeature.set("iconSrc", IconComponent);
            iconFeature.set("deviceType", type);
            iconFeature.setStyle(
                new Style({
                    image: new Icon({
                        anchor: [0.5, 1],
                        src: IconComponent,
                        scale: 1.2,
                        offset: [0, 3, 0, 0]
                    }),
                    text: new Text({
                        text: device_name,
                        font: "12px Arial",
                        fill: new Fill({ color: trackColor }),
                        backgroundFill: new Fill({ color: "rgba(255, 255, 255, 0.6)" }),
                        backgroundStroke: new Stroke({ color: trackColor, width: 1 }),
                        padding: [2, 2, 2, 2],
                        offsetY: -40,
                    }),
                })
            );

            vectorSource.current.addFeature(iconFeature);
            vectorSource.current.addFeature(pointFeature);
        });
    }, [mapRef, vectorSource, deviceColors]);


    const removeOutliers = (points: GeoData[], threshold: number = 300) => {
        if (points.length < 4) return points;

        const filteredPoints = [];

        for (let i = 1; i < points.length - 1; i++) {
            const prev = points[i - 1];
            const current = points[i];
            const next = points[i + 1];

            const d1 = getDistance([prev.longitude, prev.latitude], [current.longitude, current.latitude]);
            const d2 = getDistance([current.longitude, current.latitude], [next.longitude, next.latitude]);

            if (d1 < threshold && d2 < threshold) {
                filteredPoints.push(current);
            }
        }

        return [points[0], ...filteredPoints, points[points.length - 1]];
    };



    const recalculateTrack = useCallback(() => {
        if (!mapRef.current || !vectorSource.current) return;

        vectorSource.current.clear();
        setDeviceTracks({});

        Object.keys(historyPoints).forEach((device) => {
            const rawPoints = historyPoints[device];
            if (!rawPoints || rawPoints.length === 0) return;

            if (rawPoints.length >= 2) {
                const cleanedPoints = removeOutliers(rawPoints, OUTLIER_THRESHOLD);
                if (cleanedPoints.length < 2) return;
                addTrackPointsToMap(cleanedPoints);
            } else {
                addTrackPointsToMap(rawPoints);
            }
        });

        mapRef.current.getLayers().forEach((layer) => {
            if (layer instanceof VectorLayer) {
                layer.getSource()?.changed();
            }
        });

    }, [historyPoints, vectorSource, mapRef, addTrackPointsToMap]);

    useEffect(() => {
        if (!mapRef.current) return;

        const allFeatures = vectorSource.current.getFeatures();

        allFeatures.forEach((feature) => {
            const deviceType = feature.get("deviceType");
            if (deviceType && !deviceTypes.includes(deviceType)) {
                if (!hiddenTracks.current[deviceType]) {
                    hiddenTracks.current[deviceType] = [];
                }

                hiddenTracks.current[deviceType].push(feature);
                vectorSource.current.removeFeature(feature);
            }
        });

        Object.entries(hiddenTracks.current).forEach(([deviceType, features]) => {
            if (deviceTypes.includes(deviceType)) {
                features.forEach((feature) => {
                    vectorSource.current.addFeature(feature);
                });

                delete hiddenTracks.current[deviceType];
            }
        });

        mapRef.current.getLayers().forEach((layer) => {
            if (layer instanceof VectorLayer) {
                layer.getSource()?.changed();
            }
        });
    }, [deviceTypes]);

    useEffect(() => {
        if (mapRef.current) {
            routeRenderers.current.forEach((renderer) => renderer.destroy());
            routeRenderers.current = routes.map((route) => new RouteRenderer(mapRef.current!, route));
        }
    }, [routes]);

    useEffect(() => {
        if (!mapRef.current) {

            const container = mapContainerRef.current;
            if (!container) return;

            mapRef.current = new Map({
                target: container,
                layers: [
                    new TileLayer({
                        source: new XYZ({
                            url: "https://{a-c}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png",
                        })
                    }),
                    new VectorLayer({ source: vectorSource.current }),
                ],
                view: new View({
                    center: fromLonLat([37.488646, 55.531162]),
                    zoom: 15,
                }),
            });

            mapRef.current.on("click", (event) => {
                const clickedFeature = mapRef.current?.forEachFeatureAtPixel(event.pixel, (feature) => feature);
                if (clickedFeature && clickedFeature.get("timestamp")) {
                    const timestamp = formatMoscowTime(clickedFeature.get("timestamp"));

                    setTooltipContent(`${timestamp}`);
                    const pixel = mapRef.current?.getPixelFromCoordinate(event.coordinate);
                    if (pixel) {
                        setTooltipPosition([pixel[0], pixel[1] - 10]);
                    }

                } else {
                    setTooltipContent(null);
                }
            });
        }

        const map = mapRef.current;

        map.on("pointermove", (event) => {
            const hoveredFeature = map.forEachFeatureAtPixel(event.pixel, (feature) => feature)  as Feature | null;;
            if (hoveredFeature) {
                const isPoint = hoveredFeature.get("timestamp");

                if (isPoint) {
                    if (hoveredFeature) {
                        hoveredFeature.setStyle(
                            new Style({
                                image: new CircleStyle({
                                    radius: 9,
                                    fill: new Fill({color: hoveredFeature.get("color")}),
                                    stroke: new Stroke({color: "white", width: 2}),
                                }),
                            })
                        );
                    }
                }

                map.getTargetElement().style.cursor = "pointer";
            } else {
                map.getTargetElement().style.cursor = "";


                vectorSource.current.getFeatures().forEach((feature) => {
                    const isPoint = feature.get("timestamp");

                    if (isPoint) {
                        feature.setStyle(
                            new Style({
                                image: new CircleStyle({
                                    radius: 6,
                                    fill: new Fill({ color: feature.get("color") }),
                                    stroke: new Stroke({ color: "white", width: 1 }),
                                }),
                            })
                        );
                    }
                });
            }
        });

        return () => {
            map.un("pointermove", () => {});
        };

    }, []);


    useEffect(() => {
        if (!newPoint || !deviceTypes.includes(newPoint.type)) return;
        setHistoryPoints((historyPoints) => {
            return { ...historyPoints, [newPoint.device]: [...(historyPoints[newPoint.device] || []), newPoint] };
        })
    }, [newPoint, deviceTypes, deviceColors]);

    useEffect(() => {
        if (Object.keys(historyPoints).length > 0) {
            recalculateTrack();
        }
    }, [historyPoints, recalculateTrack]);

    return (
        <div>
            <div className="relative">
                <div ref={mapContainerRef} className="w-full h-[600px] border rounded-lg"/>
                {tooltipContent && tooltipPosition && (
                    <Tooltip>
                        <div className="absolute bg-black text-white p-2 rounded-md"
                             style={{left: tooltipPosition[0], top: tooltipPosition[1]}}>
                            {tooltipContent}
                        </div>
                    </Tooltip>
                )}
            </div>
        </div>
    );
};

export default GeoMap;
